/**
 * Human-facing reference content for each reconciliation step, ported from the
 * Contract & Billing Playbook's "Monthly Reconciliation" tab. Keyed by the same
 * step_key the backend is authoritative for (title/mode/owner_roles/status come
 * from the API; this file supplies the prose the step detail page renders).
 */

import type { ReconRole } from "./api";

export interface StepDetailRow {
  label: string;
  body: string;
}

export interface StepContent {
  /** One-line "what happens" summary. */
  tagline: string;
  /** Labeled rows mirroring the playbook step card (Inputs / Logic / Writes / …). */
  detail: StepDetailRow[];
  /** True for proposed/not-yet-built mechanisms (R-10). */
  toBuild?: boolean;
}

export const STEP_CONTENT: Record<string, StepContent> = {
  "R-00": {
    tagline:
      "The controller attests that ALL of last month's invoicing is entered and final in QuickBooks — the close's entry gate.",
    detail: [
      {
        label: "Inputs",
        body: "QuickBooks: every invoice, credit memo, and adjustment for the month under close, entered and final.",
      },
      {
        label: "Logic",
        body: "Human attestation, not a computation: the controller confirms nothing is still being invoiced or corrected. Marking this step complete IS the sign-off.",
      },
      {
        label: "Writes",
        body: "Completion stamp (who/when). Unlocks R-01 — the billed-actuals pull is blocked with a 409 until this step is complete.",
      },
      {
        label: "Watch out",
        body: "Sign off only when invoicing is truly done — R-01 snapshots what it sees as the month's FINAL billing numbers, and every later step validates against them. If something slips in late, reopen this step, fix QuickBooks, and re-pull R-01 (unchanged accounts keep their review verdicts).",
      },
    ],
  },
  "R-01": {
    tagline:
      "Ingest the just-closed month's billed revenue — the ground truth every later step reconciles against.",
    detail: [
      {
        label: "Inputs",
        body: "BI.REVENUE_WATERFALL — TRANSACTION_PERIOD, COUNTERPARTY_ID, AMOUNT_CURRENT, IS_MRR_SUBSCRIPTION.",
      },
      {
        label: "Logic",
        body: "Billed MRR for the period = SUM(AMOUNT_CURRENT) WHERE IS_MRR_SUBSCRIPTION=1. Resolve each contract to its counterparty via the join chain (ACCOUNT_ID, or ASSIGNED_COMPANY_ID for POs → ACCOUNTS.QUICKBOOKS_ID → COUNTERPARTY_ID).",
      },
      { label: "Writes", body: "Per-contract billed MRR for the period into the reconciliation snapshot." },
      {
        label: "Watch out",
        body: "Waterfall + accounts refresh nightly — run after the sync settles. Bundled POs share one counterparty and are separated downstream (the bundled-PO contractions and sub-account roll-up steps). Never anchor on max(period) — the waterfall carries pre-populated $0 future rows; anchor on the last complete billed month.",
      },
    ],
  },
  "R-14": {
    tagline:
      "Confirm every contract signed in the close month is entered — live from the Cinch API, not a nightly mirror.",
    detail: [
      {
        label: "Inputs",
        body: "Cinch API billing/contracts filtered server-side by sign_date to the close month (the same table the Contracts feature shows, with Billing Effective in place of Renewal).",
      },
      {
        label: "Logic",
        body: "Human confirmation, informed by the tiles: total contracts signed and their MRR/ARR. Anything sold that isn't in this list means the contract record is incomplete — chase it down before moving on.",
      },
      {
        label: "Writes",
        body: "Completion stamp only — marking this step complete is the attestation that the month's signed-contract record is complete, and it locks the step.",
      },
      {
        label: "Watch out",
        body: "This reads the LIVE Cinch database on purpose (Snowflake's billing_contracts mirror lags ~24h). A contract missing its billing effective date will surface again in the next step's worklist.",
      },
    ],
  },
  "R-15": {
    tagline:
      "Structural integrity of the billing graph — every company, account, contract and PO connected to what it should be.",
    detail: [
      {
        label: "Inputs",
        body: "The Cinch app's companies and accounts, BILLING.BILLING_CONTRACTS, and QuickBooks counterparties for the ID check — read live on every load, plus a three-month billing lookback to tell which contract-less accounts are actually taking money.",
      },
      {
        label: "Logic",
        body: "Ten checks, each a list of exceptions: every company assigned to a live account; every account carrying a QuickBooks ID that resolves to a real counterparty; every contract attached to a live account; every account with at least one contract; and every purchase order tied to both a company and an actual Reseller Agreement. Money-affecting breaks sort first — an account billing with no contract behind it leads the list.",
      },
      {
        label: "Writes",
        body: "Per-exception verdicts only. Nothing is auto-corrected: fix the record in Cinch and it drops off this list on the next load, with no re-pull. Completing the step freezes whatever is still broken as the audit copy.",
      },
      {
        label: "Watch out",
        body: "Run live 2026-08-14 this found 257 exceptions — including 12 accounts billing $26K with no contract behind them, 57 of 110 purchase orders with no master contract, and 24 contracts pointing at deleted accounts. The PO gaps are the blocker for the bundled-PO and sub-account roll-up steps, which can't be built honestly until they're cleaned up. Most of the 94 contract-less accounts are internal or long dead; the billing filter is how you find the ones that aren't.",
      },
    ],
  },
  "R-02": {
    tagline:
      "Establish each contract's true billing start and preserve billing tenure across replacements.",
    detail: [
      {
        label: "Inputs",
        body: "Two reviewed worklists — (1) accounts billed for the FIRST time this month, with the newest contract's billing effective date and the actual first invoice date; (2) contracts missing a billing effective date that have never billed.",
      },
      {
        label: "Logic",
        body: "First-time billers should have a billing effective date IN the close month and a first invoice that matches it — the default view shows only rows where they don't line up. Approve what's expected (e.g. deliberate delayed starts); flag what's wrong. For replacements, tenure traces to the root of the chain — continuous billing, no churn-and-restart.",
      },
      {
        label: "Writes",
        body: "Per-row verdicts into the close's snapshot. Fixes themselves happen in Cinch (billing effective dates), then re-pull. Flags block completing this step.",
      },
      {
        label: "Watch out",
        body: "Bundled POs need line-item attribution. The term-anchor basis (term starts at billing start) is pending CS sign-off + a contract-language update.",
      },
    ],
  },
  "R-03": {
    tagline: "Classify every active contract and quantify the booked-vs-billed dollar gap.",
    detail: [
      {
        label: "Inputs",
        body: "BILLING.BILLING_CONTRACTS joined to BI.REVENUE_WATERFALL: every non-PO contract whose account has never billed a dollar, with its billing effective date, sign date and onboarding flags.",
      },
      {
        label: "Logic",
        body: "Each never-billed contract is classified by how overdue billing is, measured from when it SHOULD have started (billing effective date, or sign date when that's missing) to the close month end — Not started yet (effective next month, fine), Should have billed (late, chase it), or Debook candidate (3+ months overdue, or onboarding abandoned). Gap = the contracted MRR that isn't being billed.",
      },
      {
        label: "Writes",
        body: "Per-contract verdict for the period, plus DEBOOK decisions — which are durable: a debooked contract drops off this and every future month's list until someone reverses it, and the reason, who and when are kept.",
      },
      {
        label: "Watch out",
        body: "Billing is tracked per account, so \"never billed\" means the account never billed — exact for a first contract, conservative for an upsell on an already-billing account (per-line attribution isn't built). POs are excluded: bundled POs bill under their reseller by design. Debooking is recorded in cinch_ops because Cinch's billing mart has no debooked column for anything downstream to filter on. Leaving a candidate booked is a legitimate outcome — only an open flag blocks completing the step.",
      },
    ],
  },
  "R-04": {
    tagline:
      "Catch accounts that were billing and have gone to $0 — and settle when they actually left, because no system reliably records it.",
    detail: [
      {
        label: "Inputs",
        body: "Billed-MRR history from BI.REVENUE_WATERFALL, plus all three places a churn date might live: the billing contract (CONTRACT_CANCELED_AT / STOP_BILLING_AT), HubSpot (cancellation notification + churn date), and the Cinch company record (CANCEL_DATE). Account lineage from the renewals account links tells a merge apart from a departure.",
      },
      {
        label: "Logic",
        body: "Ever billed AND $0 now → a candidate, reaching back three months so nothing dispositioned falls off the end. Each one is classified: merged away (lineage says the revenue moved), netted to $0 (billed then credited back the same month), a date is on record, or nobody recorded anything. You settle two dates per account — notice date (when they told us) and end date (last day we bill) — seeded from whichever source has them, and overridable.",
      },
      {
        label: "Writes",
        body: "A durable churn record per account: outcome, notice date, end date, reason, MRR at churn. It is NOT period-scoped — later closes don't re-ask, and the cash forecast reads the end date to stop the revenue. Flags block completing the step.",
      },
      {
        label: "Watch out",
        body: "Run against the July 2026 close, this found 21 accounts that stopped billing — and HubSpot was the ONLY source with a date for any of them (11 of 21). Zero carried a billing-contract cancel or stop date; zero had a company cancel date. The remaining 10 accounts, worth $41,923 of monthly revenue, simply stopped paying with no recorded reason anywhere, the largest at $35,792. That gap is the worklist. Also watch \"billed short\" — an end date later than our last invoice means a month we were owed and never billed (10 of the 21 here). Merges are excluded from churn totals, but only where someone recorded the lineage link: an unlinked account stays on the list however big it is, because assuming a large account \"probably merged\" is how real churn gets hidden. Bundled POs never show $0 and are handled in the bundled-PO contractions step.",
      },
    ],
  },
  "R-05": {
    tagline:
      "Attribute partner-level billed drops to the specific cancelled PO, since bundled POs never show $0.",
    detail: [
      { label: "Inputs", body: "Partner-level billed totals; the Partner team's cancelled-PO flags captured during the month." },
      { label: "Logic", body: "Match a reduction on the reseller's total to a flagged PO/company and record the end on that child contract." },
      { label: "Writes", body: "PO-level churn/end on the correct child PO." },
      {
        label: "Watch out",
        body: "Depends on the Partner team flagging cancellations in-month; without the flag a drop looks like an unexplained contraction.",
      },
    ],
  },
  "R-06": {
    tagline: "Find mismatches between billing reality and recorded intent — in both directions.",
    detail: [
      {
        label: "Logic",
        body: "(a) Billing stopped but no notice on record → flag CS to backfill notice + reason. (b) Notice/cancel on record but billing continues → flag to verify it actually ended.",
      },
      { label: "Writes", body: "Data-quality flags and corrected dates." },
      { label: "Watch out", body: "This is the safety net that catches anything the direct-churn and bundled-PO steps mis-handled." },
    ],
  },
  "R-07": {
    tagline: "Identify signed deals that never billed and have been abandoned.",
    detail: [
      { label: "Inputs", body: "Status, ever-billed, contract age, IS_ONBOARD_ABANDONED." },
      {
        label: "Logic",
        body: "Booked AND never billed AND (age ≥ 3 months OR onboarding abandoned) → candidate. A human confirms before debooking.",
      },
      { label: "Writes", body: "On confirm, DEBOOKED = true + DEBOOKED_REASON; removed from booked-unbilled." },
      { label: "Watch out", body: "Debooking is deliberate — never automatic — to avoid dropping a slow-starting deal." },
    ],
  },
  "R-08": {
    tagline: "Net out replacements so a restructure isn't double-counted or mistaken for churn.",
    detail: [
      {
        label: "Inputs",
        body: "Replacements signed this month, the contract each one replaces plus any upsells amending it, and what the account was actually billed the month before signing.",
      },
      {
        label: "Logic",
        body: "Net change = the new contract's MRR less what the account was actually billed in the period before it was signed (subscription items) — billed reality, not contracted intent, so revenue the customer was already handing over is never counted as new. The expanded drawer shows that difference item by item, and the columns foot to the headline. Separately, confirm the replaced contract was closed the day before the replacement starts.",
      },
      {
        label: "Verifies",
        body: "Cinch closes the replaced contract automatically when the replacement is created, so this step checks it actually happened. A replaced contract left open means both are billing on the account and booked ARR counts it twice.",
      },
      {
        label: "Carry-in",
        body: "Broken suppressions from any earlier month stay on this list until they're fixed — a stranded contract shouldn't wait for someone to reopen the month it happened in. They don't count toward this month's net.",
      },
      {
        label: "Watch out",
        body: "Only 'Replacement Contract' carries a replaced-contract link; PO, Items, Reseller and License replacements have none, so their suppression can't be verified. Purchase-order replacements also sit on the reseller's account, so no baseline is meaningful — those stay unvalued and are reviewed by hand.",
      },
      {
        label: "Writes",
        body: "Verdicts and the settled net only. Cinch's billing API is superuser-only, so the fix is applied in Cinch and confirmed here on the next read.",
      },
    ],
  },
  "R-09": {
    tagline: "Recompute the forward renewals pipeline.",
    detail: [
      {
        label: "Logic",
        body: "NEXT_RENEWAL_AT = term anchor + N × term, rolling. Exceptions: IS_AUTO_RENEW=false (expires); RENEWAL_TERM=MONTH_TO_MONTH (apply MTM_UPLIFT_PCT). Surface contracts whose renewal minus the notice window lands in the coming month.",
      },
      { label: "Writes", body: "NEXT_RENEWAL_AT; the renewals pipeline." },
      { label: "Watch out", body: "Clean same-term renewals roll evergreen (no record); renewals with changes are Replacements (the replacement supersession step)." },
    ],
  },
  "R-10": {
    tagline: "Tie POs that bill under their own QBID up to their reseller as sub-accounts.",
    toBuild: true,
    detail: [
      { label: "Logic", body: "Map each PO's own QBID/account under its RESELLER_CONTRACT_ID so partner-level and per-PO views both reconcile." },
      { label: "Writes", body: "Sub-account → reseller mapping." },
      {
        label: "Watch out",
        body: "Proposed — no mechanism today; until built, POs with their own QBID need a manual roll-up.",
      },
    ],
  },
  "R-16": {
    tagline:
      "Confirm the parent/child company tree is right before it shapes roll-ups and reporting.",
    detail: [
      {
        label: "Inputs",
        body: "The Parent Companies monitor's approval workflow — every relationship change with activity in the close month (requested, approved, verified live in Cinch, rejected or withdrawn), plus the latest AI review flag on each parent group. Read live from the workflow's own tables, so there's no pull.",
      },
      {
        label: "Logic",
        body: "Walk the month's changes. For each one, confirm the child really belongs under that parent — a wrong tree misstates partner-level revenue and can make a merge look like churn. The month comes from the CHANGE LOG, not the request date: a link requested in June and verified in July is July's business, because July is when the tree actually moved, so one request can be reviewed in two closes with its own verdict in each. Watch the Waiting on Cinch count hardest: an approved change nobody has applied leaves every roll-up reading the old shape.",
      },
      {
        label: "Writes",
        body: "A per-change verdict (Verified / Flagged) with the reviewer's name and notes on the row. Completing the step freezes the month's changes as the audit copy of what was attested.",
      },
      {
        label: "Watch out",
        body: "Cinch has no write API for company relationships: applying a change is a Django-admin action verified on read, and it takes two-person approval, so plan for a lag between deciding and seeing it reflected — that lag is exactly what the waiting rows are. Nothing is corrected here; a fix is a new request in the Parent Companies monitor. And the AI flag is on the whole parent GROUP, not this one change — treat it as a prompt to look closer, never as the verdict.",
      },
    ],
  },
  "R-17": {
    tagline:
      "Verify which companies sit under each internal account — their usage is billed to that department's budget.",
    detail: [
      {
        label: "Inputs",
        body: "A stored roster of the Cinch-owned accounts (ten today), every company under them, and each company's contacts and text usage for the close month — plus the reverse sweep over companies on customer accounts that look internal.",
      },
      {
        label: "Logic",
        body: "Every company on a roster account gets a verdict: does it belong there, and does its usage belong to that department's budget? A customer company mis-parented to an internal account is revenue we never billed — July 2026 found South Bay Lube on Cinch - Sales with 34 locations while the real, billing South Bay Lube sat on its own account. An internal company left on a customer account bills someone else for our own usage, which is what the Recommended tab hunts for, on two signals: the company name matches the internal vocabulary, and/or every non-superuser on it is @cinch.io.",
      },
      {
        label: "Writes",
        body: "A per-company verdict with the reviewer's name on the row, the department each account's usage is charged to, and a durable dismissal for any candidate ruled not-internal so it never returns.",
      },
      {
        label: "Watch out",
        body: "The roster is a STORED list, not a query on ACCOUNT_TYPE = 'Internal' — that tag covers only six of the ten, so deriving it would silently drop Cinch, Cinch - R&D, Justin made companies and Project Upskill. And zero revenue is NOT an internal signal: Moxie Pest Control bills $0 under its own QuickBooks id because the money posts to the merged counterparty Moxie Advisors, so account folding would make every merged customer look like ours. Overlaps with the general billing data validation step: fix the assignment there, confirm the department here.",
      },
    ],
  },
  "R-18": {
    tagline:
      "Make sure the integration fees we pay are for customers who are still paying us.",
    detail: [
      {
        label: "Inputs",
        body: "Each vendor's invoice for the month (recorded on the Invoices tab, with the service period it actually covers), matched against Cinch locations, the transactions those locations sent in that window, and the outcomes of the churn and billed-actuals steps.",
      },
      {
        label: "Logic",
        body: "Three modes, decided by what the vendor prints. LINE vendors (ISI, PM Attendant, RO Writer) bill one line per location: each is matched on the vendor's own location id and gets a verdict. COUNT vendors (Vehlo, Sage, Autodata, Egenuity) state only a quantity, so the finding is the delta against connections we can evidence. LISTED vendors (MOTOR, AutoNetTV) are recorded so the month's total is complete but never matched — MOTOR is a flat licence, AutoNetTV a content plugin on its own id space.",
      },
      {
        label: "Writes",
        body: "A per-line verdict (ok / no data / churned / unmapped / duplicate / cancellation not honoured), the durable vendor-ref → Cinch-location map, and a disposition per exception (cancel requested / keep / already cancelled) with the month we expect it off the invoice.",
      },
      {
        label: "Watch out",
        body: "Never infer the service period from the invoice date — ISI bills July on a 7/31 invoice, PM Attendant bills August in advance on 8/1, and the Driverse vendors bill July on an 8/12 invoice. Cancellations lag a billing cycle, so a line you actioned reappears once; recording a disposition is what turns that into 'cancellation not honoured' instead of a fresh finding. And a line's evidence is the transactions it sent — never the company's IS_ACTIVE flag, which is false on plenty of live, heavily-transacting customers.",
      },
    ],
  },
  "R-19": {
    tagline:
      "The controller attests every expense invoice for the month is in — the books can close.",
    toBuild: true,
    detail: [
      {
        label: "Inputs",
        body: "QuickBooks bills, vendor credits and card/ACH purchases for the month under close.",
      },
      {
        label: "Logic",
        body: "Human attestation, the expense-side twin of the invoicing-final step: the controller confirms nothing is still to be entered or accrued. Marking this step complete is the sign-off, and it's what makes the budget-holder review below meaningful.",
      },
      { label: "Writes", body: "Completion stamp (who/when). Intended to gate the budget-holder review that follows." },
      {
        label: "Watch out",
        body: "Placeholder — the gate isn't enforced yet (the invoicing-final step already works this way and is the model to copy). Sign off only once accruals are booked; anything landing later reopens this step and the reviews after it.",
      },
    ],
  },
  "R-20": {
    tagline:
      "Each budget holder reviews the expenses charged to their budget and confirms they've been checked.",
    toBuild: true,
    detail: [
      {
        label: "Inputs",
        body: "The month's classed expense lines from Budget vs Actual, split by department — each holder sees only their own budget (the department grants already model this).",
      },
      {
        label: "Logic",
        body: "Per department: review the lines charged to your budget, confirm each is yours and correctly coded, and flag what isn't. The step completes when every department with expenses has confirmed — so completion is per-owner, not a single verdict.",
      },
      { label: "Writes", body: "Per-department confirmation plus flags on individual expense lines." },
      {
        label: "Watch out",
        body: "Placeholder — per-department sub-completion is a new shape for these steps (today a step has one status). Depends on the expenses-all-in attestation landing first, or holders review a moving target.",
      },
    ],
  },
  "R-12": {
    tagline:
      "Confirm the month's commission payouts, each rep's year-to-date quota attainment, and any kicker adjustments before sign-off.",
    detail: [
      {
        label: "Inputs",
        body: "The month's cash-received subscription billing (commissions are paid cash-on-cash); each contract's commission assignments (person, rate, payout window); annual quotas; the commission workbook until the Commissions feature replaces it.",
      },
      {
        label: "Logic",
        body: "Verify every commissionable billing line paid the right people at the right rate and is inside its payout window (M1–M12 + P1, plus any earned 13th month). Update year-to-date quota attainment per rep and apply kicker rate adjustments for anyone above quota — kickers are a manual determination for now, recorded per contract.",
      },
      {
        label: "Writes",
        body: "Locked-in payout amounts for the month (once the Commissions feature lands, these persist per person per contract so history is exactly reproducible), plus any per-contract rate adjustments from kickers.",
      },
      {
        label: "Watch out",
        body: "Low-margin contracts should reduce the commission rate (the proposals margin flag has no contracts-side equivalent yet — planned, not built). Clawbacks for mid-term churn are also not built; today a churned account simply stops generating cash, so payouts stop with it.",
      },
    ],
  },
  "R-13": {
    tagline:
      "Review the accounts that outgrew their contracted contact allotment and approve who gets billed the expansion overage this month.",
    toBuild: true,
    detail: [
      {
        label: "Inputs",
        body: "The Expansion feature's per-account overage: contracted contacts, current usage, contacts over, currently billed, and the additional amount to bill.",
      },
      {
        label: "Logic",
        body: "Surface each over-allotment account and the proposed additional monthly charge; an owner approves or holds each one. Approvals are the list of accounts we actually raise expansion billing for.",
      },
      { label: "Writes", body: "Per-account approve/hold decision for the period's expansion billing (stub — schema wired in a later session)." },
      {
        label: "Watch out",
        body: "Stub only — the review/approve UI and its persistence land when the rest of the reconciliation steps are built out. Numbers still exclude the merged-account and duplicate-company cleanups in progress.",
      },
    ],
  },
  "R-11": {
    tagline: "Publish the month's numbers and obtain sign-off.",
    detail: [
      {
        label: "Outputs",
        body: "Combined booked + billed ARR, billed ARR/MRR, booked-vs-billed tracker, churn log (notice + end dates + reason), debooked list, renewals pipeline.",
      },
      {
        label: "Sign-off",
        body: "CRO, CS, RevOps and Finance review; approved & called complete by the approver — before the 7th.",
      },
      { label: "Watch out", body: "Nothing is final until sign-off; a late waterfall correction re-opens the affected step." },
    ],
  },
};

export const RECON_ROLE_LABEL: Record<ReconRole, { short: string; person: string }> = {
  cs: { short: "CS", person: "J-P Scoville" },
  revops: { short: "RevOps", person: "Waylon" },
  cro: { short: "CRO", person: "Stan James" },
  finance: { short: "Finance", person: "Cody & Chace" },
  partner: { short: "Partner", person: "Gabe Davis" },
  controller: { short: "Controller", person: "Cody" },
  // Department budget holders — assigned per person on the Admin tab rather
  // than being one named owner.
  budget_owner: { short: "Budget holder", person: "per department" },
  approver: { short: "Sign-off", person: "Justin" },
};

/**
 * StatusBadge props for a step's status chip. A step's "pending" is a dormant
 * not-started state, not an awaiting-action alert — so it maps to the DS's
 * neutral `not_started` value (keeping the "Pending" wording) instead of the
 * amber `pending` used by approval queues. Every other status maps directly:
 * in_progress → warning, complete → success, blocked → danger, N/A → neutral.
 */
export function stepStatusChip(status: string): { value: string; label?: string } {
  if (status === "pending") return { value: "not_started", label: "Pending" };
  return { value: status };
}

/**
 * The step NUMBER shown in the UI is the step's POSITION (order − 1, zero
 * padded), not its stable key: keys are never renumbered, so when a step is
 * inserted (R-14 "All signed contracts in" at position 2) every following
 * step's visible number shifts while URLs and data stay keyed by R-xx.
 */
export function stepDisplayNo(order: number): string {
  return String(order - 1).padStart(2, "0");
}

export function modeLabel(mode: string): string {
  if (mode === "system") return "System";
  if (mode === "system_then_human") return "Sys → Human";
  if (mode === "human") return "Human";
  return mode;
}
